import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Polish is the fallback and the main audience: bundled with the app. English and German
// are separate chunks loaded only when active (issue #236, F14) — each locale is ~170 kB
// of JSON that every visitor used to download three times over.
import pl from './locales/pl.json';

type Translations = Record<string, unknown>;

export const SUPPORTED_LANGUAGES = ['pl', 'en', 'de'] as const;

const lazyLocales: Record<string, () => Promise<{ default: Translations }>> = {
    en: () => import('./locales/en.json'),
    de: () => import('./locales/de.json'),
};

/** 'en-US' → 'en'; unsupported → 'pl' (the fallback). */
export const baseLanguage = (lng: string | undefined): string => {
    const base = (lng || '').split('-')[0].toLowerCase();
    return (SUPPORTED_LANGUAGES as readonly string[]).includes(base) ? base : 'pl';
};

const pending = new Map<string, Promise<void>>();

/** Makes sure the translations of `lng` are in the store (no-op for bundled ones). */
export const loadLanguage = (lng: string | undefined): Promise<void> => {
    const base = baseLanguage(lng);
    if (i18n.hasResourceBundle(base, 'translation') || !lazyLocales[base]) return Promise.resolve();
    let request = pending.get(base);
    if (!request) {
        request = lazyLocales[base]()
            .then((module) => {
                i18n.addResourceBundle(base, 'translation', module.default, true, true);
            })
            .catch((error) => {
                // Missing chunk (offline, deploy in between): the Polish fallback still renders
                console.error(`[i18n] Failed to load "${base}" translations`, error);
            })
            .finally(() => pending.delete(base));
        pending.set(base, request);
    }
    return request;
};

i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
        resources: {
            pl: { translation: pl },
        },
        partialBundledLanguages: true,
        fallbackLng: 'pl',
        interpolation: {
            escapeValue: false,
        },
        // Re-render when a lazily loaded language arrives
        react: {
            bindI18nStore: 'added',
        },
        detection: {
            order: ['localStorage', 'navigator'],
            caches: ['localStorage'],
            lookupLocalStorage: 'i18nextLng',
        },
    });

// Switching language loads its translations; react-i18next re-renders on 'added'
i18n.on('languageChanged', (lng) => { void loadLanguage(lng); });

/** Resolves once the detected language can render (immediately for Polish). */
export const i18nReady: Promise<void> = loadLanguage(i18n.language);

export default i18n;
